import { Check, ChevronRight, LoaderCircle } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * One collapsible card of the booking form. Collapsed it is a one-line review
 * row — title, a summary of what the AI filled in, and either a green check or
 * an orange "N missing" badge — so the agent scans the whole booking at a
 * glance and only opens what needs work. A card with missing fields is also
 * tinted, which is what makes the gap findable while collapsed.
 */
export function FormSection({
  title,
  summary,
  missingCount,
  busy = false,
  open,
  onToggle,
  bodyRef,
  children,
}: {
  title: string;
  /** Shown only while collapsed — the open card shows the real fields. */
  summary: string;
  missingCount: number;
  /** Still resolving (addresses, vehicle list) — show a spinner, not a verdict. */
  busy?: boolean;
  open: boolean;
  onToggle: () => void;
  bodyRef?: (el: HTMLElement | null) => void;
  children: ReactNode;
}) {
  return (
    <section className={`aib-sec${missingCount > 0 && !busy ? ' need' : ''}`} ref={bodyRef}>
      <button type="button" className="aib-sec-head" aria-expanded={open} onClick={onToggle}>
        <ChevronRight size={14} className={`aib-sec-chev${open ? ' open' : ''}`} />
        <span className="aib-sec-title">{title}</span>
        {!open && summary ? <span className="aib-sec-sum">{summary}</span> : null}
        {busy ? (
          <LoaderCircle size={12} className="spin-anim aib-sec-busy" />
        ) : missingCount > 0 ? (
          <span className="aib-sec-badge">
            {missingCount} missing
          </span>
        ) : (
          <span className="aib-sec-ok">
            <Check size={12} /> done
          </span>
        )}
      </button>
      {open && <div className="aib-sec-body">{children}</div>}
    </section>
  );
}
